import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { S, Field, Select, Modal, SearchSelect, itemOptions, fmtQty, fmtMoney, fmtDate, today } from './StoresShared';
import { useList } from './StoresPurchase';
import { MoveDocView } from './StoresMovements';

// Stores module — Phase 3 screens: Linen, Room Consumption, PM Kits, Fuel Log,
// and the Settings pieces for moving amenity / spare-part stock into Stores.
// API: backend/v1/routes/storesOps.js.

const n = (v) => Number(v) || 0;
const r4 = (v) => Math.round(n(v) * 10000) / 10000;
const key = () => Math.random().toString(36).slice(2);
const small = { fontSize: '0.72rem', color: '#94a3b8' };
const can = (boot, ...roles) => boot.me.is_hotel_admin || roles.some((r) => boot.me.roles.includes(r));
const monthStart = () => `${today().slice(0, 8)}01`;
const unitsOf = (it) => (it ? [it.base_uom, ...(it.uoms || []).map((u) => u.uom_code)] : []);

function useItems(api, filter) {
  const [items, setItems] = useState([]);
  useEffect(() => { api('/items?active=1').then((rows) => setItems(filter ? rows.filter(filter) : rows)).catch(() => setItems([])); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  return { items, byId };
}

// ── Linen ────────────────────────────────────────────────────────────────
const LINEN_ACTIONS = [
  ['ISSUE', 'Issue from store to floors / rooms', 'Taken out of the store into circulation.'],
  ['TO_LAUNDRY', 'Send soiled linen to laundry', 'In use → at laundry.'],
  ['FROM_LAUNDRY', 'Receive from laundry', 'Clean pieces come back into use; enter damaged and missing pieces.'],
  ['MARK_DAMAGED', 'Mark damaged', 'In use → damaged (stained, torn).'],
  ['CONDEMN', 'Condemn damaged linen', 'Damaged pieces are written off.'],
  ['RETURN', 'Return to store', 'Back from circulation into the store.'],
];

export function Linen({ api, boot }) {
  const [data, setData] = useState({ items: [], month: {} });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const docs = useList(api, '/docs?doc_type=LINEN');
  const hkStores = boot.stores.filter((s) => s.is_active && boot.departments.find((d) => d.id === s.department_id)?.code === 'HK');
  const load = useCallback(() => { api('/linen').then(setData).catch((e) => setErr(e.message)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const start = (action) => {
    setErr(''); setMsg('');
    setForm({ action, store_id: hkStores[0]?.id || '', doc_date: today(), laundry_name: '', location_name: '', remarks: '', lines: data.items.map((i) => ({ key: key(), item_id: i.item_id, qty: '', damaged_qty: '', lost_qty: '' })) });
  };
  const post = async () => {
    setBusy(true); setErr('');
    try {
      const lines = form.lines.filter((l) => n(l.qty) > 0 || n(l.damaged_qty) > 0 || n(l.lost_qty) > 0);
      const d = await api('/linen/move', { method: 'POST', body: { ...form, lines } });
      setMsg(`${d.doc_no} posted.`); setForm(null); load(); docs.load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const setLine = (k, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === k ? { ...l, ...patch } : l)) }));
  const avail = (it) => ({ ISSUE: n(it.in_store), TO_LAUNDRY: n(it.in_use), MARK_DAMAGED: n(it.in_use), RETURN: n(it.in_use), FROM_LAUNDRY: n(it.laundry), CONDEMN: n(it.damaged) }[form?.action]);
  const byId = Object.fromEntries(data.items.map((i) => [i.item_id, i]));
  const act = form && LINEN_ACTIONS.find(([k]) => k === form.action);

  return (
    <>
      {err && !form && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {!form && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={S.card}><div style={S.lbl}>Lost this month</div><div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{fmtQty(data.month?.lost_qty)} pcs</div></div>
          <div style={S.card}><div style={S.lbl}>Written off this month</div><div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{fmtMoney(data.month?.written_off)}</div></div>
          <div style={S.card}><div style={S.lbl}>Value in circulation</div><div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{fmtMoney(data.items.reduce((s, i) => s + n(i.circulation_value), 0))}</div></div>
        </div>
      )}
      {!form && can(boot, 'STOREKEEPER', 'STORE_MANAGER', 'DEPT_HEAD') && (
        <div style={S.bar}>
          {LINEN_ACTIONS.map(([k, label]) => <button key={k} type="button" className={k === 'TO_LAUNDRY' || k === 'FROM_LAUNDRY' ? 'btn-primary' : 'btn-muted'} disabled={!data.items.length} onClick={() => start(k)}>{label}</button>)}
        </div>
      )}
      {form && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 0.3rem', fontSize: '1rem', color: '#1e3a5f' }}>{act[1]}</h3>
          <p style={{ ...S.sub, marginTop: 0 }}>{act[2]}</p>
          {err && <div style={S.err}>{err}</div>}
          <div style={S.bar}>
            {['ISSUE', 'RETURN'].includes(form.action) && (
              <Field label="Store">
                <Select style={{ ...S.inp, minWidth: 190 }} value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })}>
                  {hkStores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
                </Select>
              </Field>
            )}
            {['TO_LAUNDRY', 'FROM_LAUNDRY'].includes(form.action) && <Field label="Laundry"><input style={{ ...S.inp, width: 200 }} value={form.laundry_name} placeholder="In-house / vendor name" onChange={(e) => setForm({ ...form, laundry_name: e.target.value })} /></Field>}
            {['ISSUE', 'TO_LAUNDRY', 'MARK_DAMAGED'].includes(form.action) && <Field label="Floor / area"><input style={{ ...S.inp, width: 160 }} value={form.location_name} placeholder="e.g. Floor 2" onChange={(e) => setForm({ ...form, location_name: e.target.value })} /></Field>}
            <Field label="Date"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Linen</th><th style={{ ...S.th, ...S.num }}>Available</th>
                <th style={{ ...S.th, ...S.num }}>{form.action === 'FROM_LAUNDRY' ? 'Received clean' : 'Qty'}</th>
                {form.action === 'FROM_LAUNDRY' && <><th style={{ ...S.th, ...S.num }}>Damaged</th><th style={{ ...S.th, ...S.num }}>Missing</th></>}
              </tr></thead>
              <tbody>
                {form.lines.map((l) => {
                  const it = byId[l.item_id];
                  return (
                    <tr key={l.key}>
                      <td style={S.td}>{it.item_name}<div style={small}>{it.item_code}</div></td>
                      <td style={{ ...S.td, ...S.num }}>{fmtQty(avail(it))} {it.base_uom}</td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 90, textAlign: 'right', marginLeft: 'auto', display: 'block' }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></td>
                      {form.action === 'FROM_LAUNDRY' && <>
                        <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 80, textAlign: 'right', marginLeft: 'auto', display: 'block' }} value={l.damaged_qty} onChange={(e) => setLine(l.key, { damaged_qty: e.target.value })} /></td>
                        <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 80, textAlign: 'right', marginLeft: 'auto', display: 'block' }} value={l.lost_qty} onChange={(e) => setLine(l.key, { lost_qty: e.target.value })} /></td>
                      </>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Field label="Remarks" style={{ marginTop: '0.75rem' }}><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.9rem' }}>
            <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={post} disabled={busy}>{busy ? 'Posting…' : 'Post'}</button>
          </div>
        </div>
      )}
      {!form && (
        <>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Linen</th><th style={{ ...S.th, ...S.num }}>In store</th><th style={{ ...S.th, ...S.num }}>In use</th>
                <th style={{ ...S.th, ...S.num }}>At laundry</th><th style={{ ...S.th, ...S.num }}>Damaged</th><th style={{ ...S.th, ...S.num }}>Total</th>
                <th style={{ ...S.th, ...S.num }}>Par</th><th style={{ ...S.th, ...S.num }}>Value</th>
              </tr></thead>
              <tbody>
                {data.items.map((i) => {
                  const total = n(i.in_store) + n(i.in_use) + n(i.laundry) + n(i.damaged);
                  const below = n(i.par) > 0 && total < n(i.par);
                  return (
                    <tr key={i.item_id}>
                      <td style={S.td}><strong>{i.item_name}</strong><div style={small}>{i.item_code}</div></td>
                      <td style={{ ...S.td, ...S.num }}>{fmtQty(i.in_store)}</td><td style={{ ...S.td, ...S.num }}>{fmtQty(i.in_use)}</td>
                      <td style={{ ...S.td, ...S.num }}>{fmtQty(i.laundry)}</td>
                      <td style={{ ...S.td, ...S.num, color: n(i.damaged) ? '#b45309' : undefined }}>{fmtQty(i.damaged)}</td>
                      <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtQty(total)}</td>
                      <td style={{ ...S.td, ...S.num }}>{n(i.par) ? <>{fmtQty(i.par)} {below && <span style={S.pill('#fef3c7', '#b45309')}>Below par</span>}</> : '—'}</td>
                      <td style={{ ...S.td, ...S.num }}>{fmtMoney(n(i.store_value) + n(i.circulation_value))}</td>
                    </tr>
                  );
                })}
                {!data.items.length && <tr><td colSpan={8} style={S.empty}>No linen items yet. Add Housekeeping items with type LINEN under Items, then receive them (GRN or opening stock).</td></tr>}
              </tbody>
            </table>
          </div>
          <h4 style={{ margin: '1.25rem 0 0.5rem' }}>Recent linen movements</h4>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>No.</th><th style={S.th}>Date</th><th style={S.th}>What</th><th style={S.th}>Laundry / area</th><th style={{ ...S.th, ...S.num }}>Value</th><th style={S.th} /></tr></thead>
              <tbody>
                {docs.rows.slice(0, 50).map((d) => (
                  <tr key={d.id}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{d.doc_no}</td><td style={S.td}>{fmtDate(d.doc_date)}</td>
                    <td style={S.td}>{(LINEN_ACTIONS.find(([k]) => k === d.cost_ref_type) || [null, d.cost_ref_type])[1]}</td>
                    <td style={S.td}>{d.party_name || d.cost_ref_id || '—'}</td>
                    <td style={{ ...S.td, ...S.num }}>{fmtMoney(d.grand_total)}</td>
                    <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => api(`/docs/${d.id}`).then(setView).catch((e) => setErr(e.message))}>View</button></td>
                  </tr>
                ))}
                {!docs.rows.length && <tr><td colSpan={6} style={S.empty}>No linen movements yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {view && <MoveDocView doc={view} onClose={() => setView(null)} />}
    </>
  );
}

// ── Room consumption (housekeeping amenities) ────────────────────────────
export function RoomConsumption({ api, boot }) {
  const [f, setF] = useState({ from: monthStart(), to: today() });
  const [data, setData] = useState({ docs: [], items: [], shortages: [] });
  const [err, setErr] = useState('');
  const [view, setView] = useState(null);
  const load = useCallback(() => { api(`/room-consumption?from=${f.from}&to=${f.to}`).then(setData).catch((e) => setErr(e.message)); }, [api, f]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (id) => {
    try { await api(`/shortages/${id}/resolve`, { method: 'POST', body: {} }); load(); } catch (e) { setErr(e.message); }
  };
  const open = data.shortages.filter((s) => !s.resolved_at);
  const total = data.items.reduce((s, i) => s + n(i.value), 0);

  return (
    <>
      {err && <div style={S.err}>{err}</div>}
      <p style={{ ...S.sub, marginTop: 0 }}>Posted automatically when housekeeping confirms a room's amenities (after the amenity stock has been moved into Stores — Settings → Module moves).</p>
      <div style={S.bar}>
        <Field label="From"><input type="date" style={S.inp} value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} /></Field>
        <Field label="To"><input type="date" style={S.inp} value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} /></Field>
        <strong style={{ paddingBottom: 6 }}>Consumed {fmtMoney(total)} · {data.docs.length} room(s)</strong>
      </div>
      {open.length > 0 && (
        <div style={{ ...S.card, borderColor: '#fcd34d', background: '#fffbeb' }}>
          <h4 style={{ margin: '0 0 0.5rem', color: '#92400e' }}>Shortages to look into ({open.length})</h4>
          <p style={{ ...S.sub, marginTop: 0 }}>Housekeeping used more than the store had. Only what was in stock was taken; check the floor stock and receive or adjust, then mark resolved.</p>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>When</th><th style={S.th}>Room</th><th style={S.th}>Item</th><th style={{ ...S.th, ...S.num }}>Wanted</th><th style={{ ...S.th, ...S.num }}>Taken</th><th style={{ ...S.th, ...S.num }}>Short</th><th style={S.th} /></tr></thead>
              <tbody>
                {open.map((s) => (
                  <tr key={s.id}>
                    <td style={S.td}>{new Date(s.created_at).toLocaleString('en-IN')}</td><td style={S.td}>{s.cost_ref_id || '—'}</td>
                    <td style={S.td}>{s.item_name}<div style={small}>{s.store_name}</div></td>
                    <td style={{ ...S.td, ...S.num }}>{fmtQty(s.wanted_qty)}</td><td style={{ ...S.td, ...S.num }}>{fmtQty(s.taken_qty)}</td>
                    <td style={{ ...S.td, ...S.num, fontWeight: 700, color: '#b91c1c' }}>{fmtQty(s.short_qty)} {s.base_uom}</td>
                    <td style={S.td}>{can(boot, 'STORE_MANAGER', 'DEPT_HEAD') && <button type="button" className="btn-muted btn-sm" onClick={() => resolve(s.id)}>Mark resolved</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Amenity</th><th style={{ ...S.th, ...S.num }}>Qty used</th><th style={{ ...S.th, ...S.num }}>Rooms</th><th style={{ ...S.th, ...S.num }}>Per room</th><th style={{ ...S.th, ...S.num }}>Value</th></tr></thead>
          <tbody>
            {data.items.map((i) => (
              <tr key={i.item_code}>
                <td style={S.td}>{i.item_name}<div style={small}>{i.item_code}</div></td>
                <td style={{ ...S.td, ...S.num }}>{fmtQty(i.qty)} {i.base_uom}</td><td style={{ ...S.td, ...S.num }}>{i.rooms}</td>
                <td style={{ ...S.td, ...S.num }}>{i.rooms ? fmtQty(r4(n(i.qty) / i.rooms)) : '—'}</td><td style={{ ...S.td, ...S.num }}>{fmtMoney(i.value)}</td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={5} style={S.empty}>No room consumption in this period.</td></tr>}
          </tbody>
        </table>
      </div>
      <h4 style={{ margin: '1.25rem 0 0.5rem' }}>Rooms</h4>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>No.</th><th style={S.th}>Date</th><th style={S.th}>Room</th><th style={S.th}>Store</th><th style={{ ...S.th, ...S.num }}>Items</th><th style={{ ...S.th, ...S.num }}>Value</th><th style={S.th} /></tr></thead>
          <tbody>
            {data.docs.slice(0, 200).map((d) => (
              <tr key={d.id}>
                <td style={{ ...S.td, fontWeight: 600 }}>{d.doc_no}</td><td style={S.td}>{fmtDate(d.doc_date)}</td><td style={S.td}>{d.cost_ref_id || '—'}</td>
                <td style={S.td}>{d.store_name}</td><td style={{ ...S.td, ...S.num }}>{d.line_count}</td><td style={{ ...S.td, ...S.num }}>{fmtMoney(d.grand_total)}</td>
                <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => api(`/docs/${d.id}`).then(setView).catch((e) => setErr(e.message))}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {view && <MoveDocView doc={view} onClose={() => setView(null)} />}
    </>
  );
}

// ── PM kits ──────────────────────────────────────────────────────────────
export function PmKits({ api, boot }) {
  const { items, byId } = useItems(api);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api('/pm-kits').then(setRows).catch((e) => setErr(e.message)); }, [api]);
  useEffect(() => { load(); }, [load]);
  const mntDept = boot.departments.find((d) => d.code === 'MNT')?.id;
  const mntItems = items.filter((i) => !mntDept || i.department_id === mntDept);
  const canEdit = can(boot, 'STORE_MANAGER', 'DEPT_HEAD');

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api(`/pm-kits/${edit.schedule_id}`, { method: 'PUT', body: { lines: edit.lines.filter((l) => l.item_id && n(l.qty) > 0) } });
      setMsg(`Kit saved for ${edit.schedule_name}.`); setEdit(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const setLine = (k, patch) => setEdit((e) => ({ ...e, lines: e.lines.map((l) => (l.key === k ? { ...l, ...patch } : l)) }));
  const blank = () => ({ key: key(), item_id: '', uom_code: '', qty: '' });

  return (
    <>
      {err && !edit && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      <p style={{ ...S.sub, marginTop: 0 }}>A kit lists the spares and consumables a preventive-maintenance job needs. When the PM work order is issued from Stores → Issue (Issued to: Work order), "Load PM kit" fills the lines.</p>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>PM schedule</th><th style={S.th}>Every</th><th style={S.th}>Next due</th><th style={S.th}>Kit</th><th style={S.th} /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.schedule_id} style={r.is_active ? undefined : { opacity: 0.55 }}>
                <td style={S.td}><strong>{r.schedule_name}</strong><div style={small}>{r.category}</div></td>
                <td style={S.td}>{r.frequency_value} {String(r.frequency_type).toLowerCase()}</td>
                <td style={S.td}>{fmtDate(r.next_due_date)}</td>
                <td style={{ ...S.td, fontSize: '0.8rem' }}>{r.kit.length ? r.kit.map((k) => <div key={k.item_id}>{fmtQty(k.qty)} {k.uom_code} · {k.item_name}</div>) : <span style={{ color: '#94a3b8' }}>No kit</span>}</td>
                <td style={S.td}>{canEdit && <button type="button" className="btn-muted btn-sm" onClick={() => { setErr(''); setEdit({ ...r, lines: r.kit.length ? r.kit.map((k) => ({ key: key(), item_id: k.item_id, uom_code: k.uom_code, qty: String(n(k.qty)) })) : [blank()] }); }}>Edit kit</button>}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} style={S.empty}>No preventive-maintenance schedules. Create them in Maintenance.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && (
        <Modal title={`Kit — ${edit.schedule_name}`} onClose={() => setEdit(null)} width={760}>
          {err && <div style={S.err}>{err}</div>}
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Item</th><th style={S.th}>Unit</th><th style={{ ...S.th, ...S.num }}>Qty</th><th style={S.th} /></tr></thead>
              <tbody>
                {edit.lines.map((l) => {
                  const it = byId[l.item_id];
                  return (
                    <tr key={l.key}>
                      <td style={S.td}>
                        <SearchSelect style={{ minWidth: 260 }} value={l.item_id} options={itemOptions(mntItems)} onChange={(v) => setLine(l.key, { item_id: v, uom_code: byId[v]?.base_uom || '' })} />
                      </td>
                      <td style={S.td}><Select style={{ ...S.inp, width: 85 }} value={l.uom_code} disabled={!it} onChange={(e) => setLine(l.key, { uom_code: e.target.value })}>{unitsOf(it).map((u) => <option key={u} value={u}>{u}</option>)}</Select></td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 90, textAlign: 'right' }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></td>
                      <td style={S.td}><button type="button" className="btn-muted btn-sm" aria-label="Remove line" onClick={() => setEdit((e) => ({ ...e, lines: e.lines.length > 1 ? e.lines.filter((x) => x.key !== l.key) : [blank()] }))}>✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button type="button" className="btn-muted btn-sm" onClick={() => setEdit((e) => ({ ...e, lines: [...e.lines, blank()] }))}>+ Add line</button>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn-muted" onClick={() => setEdit(null)} disabled={busy}>Cancel</button>
              <button type="button" className="btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save kit'}</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Fuel log ─────────────────────────────────────────────────────────────
export function FuelLog({ api, boot }) {
  const { items } = useItems(api, (i) => i.item_type === 'FUEL');
  const [assets, setAssets] = useState([]);
  const [f, setF] = useState({ from: monthStart(), to: today() });
  const [data, setData] = useState({ log: [], report: [] });
  const [form, setForm] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api(`/fuel?from=${f.from}&to=${f.to}`).then(setData).catch((e) => setErr(e.message)); }, [api, f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api('/assets').then(setAssets).catch(() => setAssets([])); }, [api]);
  const item = form && items.find((i) => i.id === form.item_id);
  const stores = item ? boot.stores.filter((s) => s.is_active && s.department_id === item.department_id) : [];

  const post = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api('/fuel', { method: 'POST', body: form });
      setMsg(`${r.doc_no}: ${fmtQty(r.qty)} to ${r.asset_name}.`); setForm(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      {err && !form && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {!form && (
        <div style={S.bar}>
          {can(boot, 'STOREKEEPER', 'STORE_MANAGER') && <button type="button" className="btn-primary" disabled={!items.length} onClick={() => { setErr(''); setMsg(''); const i0 = items[0]; setForm({ item_id: i0?.id || '', store_id: boot.stores.find((s) => s.is_active && s.department_id === i0?.department_id)?.id || '', qty: '', uom_code: i0?.base_uom || '', asset_id: '', asset_name: '', meter_type: 'HOURS', meter_reading: '', fuel_date: today(), remarks: '' }); }}>+ Issue fuel</button>}
          {!items.length && <span style={S.sub}>Add a fuel item (type FUEL, e.g. Diesel in LTR) under Items and receive it first.</span>}
          <Field label="From"><input type="date" style={S.inp} value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} /></Field>
          <Field label="To"><input type="date" style={S.inp} value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} /></Field>
        </div>
      )}
      {form && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#1e3a5f' }}>Issue fuel</h3>
          {err && <div style={S.err}>{err}</div>}
          <div style={S.bar}>
            <Field label="Fuel">
              <Select style={{ ...S.inp, minWidth: 160 }} value={form.item_id} onChange={(e) => { const it = items.find((i) => i.id === e.target.value); setForm({ ...form, item_id: e.target.value, uom_code: it?.base_uom || '', store_id: boot.stores.find((s) => s.is_active && s.department_id === it?.department_id)?.id || '' }); }}>
                {items.map((i) => <option key={i.id} value={i.id}>{i.item_name}</option>)}
              </Select>
            </Field>
            <Field label="From store">
              <Select style={{ ...S.inp, minWidth: 170 }} value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <Field label={`Quantity (${form.uom_code})`}><input type="number" min="0" step="any" style={{ ...S.inp, width: 110, textAlign: 'right' }} value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></Field>
            <Field label="Date"><input type="date" style={S.inp} value={form.fuel_date} onChange={(e) => setForm({ ...form, fuel_date: e.target.value })} /></Field>
          </div>
          <div style={S.bar}>
            {assets.length > 0 && (
              <Field label="Equipment / vehicle *">
                <Select style={{ ...S.inp, minWidth: 220 }} value={form.asset_id} onChange={(e) => { const a = assets.find((x) => String(x.asset_id) === e.target.value); setForm({ ...form, asset_id: e.target.value, asset_name: a ? a.asset_name : '' }); }}>
                  <option value="">Other (type the name)</option>
                  {assets.map((a) => <option key={a.asset_id} value={a.asset_id}>{a.asset_name}{a.asset_code ? ` (${a.asset_code})` : ''}</option>)}
                </Select>
              </Field>
            )}
            {!form.asset_id && <Field label={assets.length ? 'Name' : 'Equipment / vehicle *'}><input style={{ ...S.inp, width: 200 }} value={form.asset_name} placeholder="e.g. DG Set 1, Hotel van" onChange={(e) => setForm({ ...form, asset_name: e.target.value })} /></Field>}
            <Field label="Meter">
              <Select style={S.inp} value={form.meter_type} onChange={(e) => setForm({ ...form, meter_type: e.target.value })}>
                <option value="HOURS">Running hours</option><option value="KM">Kilometres</option><option value="KWH">kWh</option><option value="NONE">No meter</option>
              </Select>
            </Field>
            {form.meter_type !== 'NONE' && <Field label="Meter reading now"><input type="number" min="0" step="any" style={{ ...S.inp, width: 130, textAlign: 'right' }} value={form.meter_reading} onChange={(e) => setForm({ ...form, meter_reading: e.target.value })} /></Field>}
          </div>
          <Field label="Remarks"><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.9rem' }}>
            <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={post} disabled={busy || !form.asset_name.trim() || !(n(form.qty) > 0)}>{busy ? 'Posting…' : 'Post fuel issue'}</button>
          </div>
        </div>
      )}
      <h4 style={{ margin: '0.5rem 0' }}>Consumption by equipment</h4>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Equipment</th><th style={{ ...S.th, ...S.num }}>Fills</th><th style={{ ...S.th, ...S.num }}>Fuel</th><th style={{ ...S.th, ...S.num }}>Value</th><th style={{ ...S.th, ...S.num }}>Meter run</th><th style={{ ...S.th, ...S.num }}>Fuel per unit</th></tr></thead>
          <tbody>
            {data.report.map((r) => (
              <tr key={r.asset_name}>
                <td style={S.td}><strong>{r.asset_name}</strong></td><td style={{ ...S.td, ...S.num }}>{r.fills}</td>
                <td style={{ ...S.td, ...S.num }}>{fmtQty(r.qty)} {r.uom}</td><td style={{ ...S.td, ...S.num }}>{fmtMoney(r.value)}</td>
                <td style={{ ...S.td, ...S.num }}>{r.run != null ? `${fmtQty(r.run)} ${String(r.meter_type).toLowerCase()}` : '—'}</td>
                <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{r.per_unit != null ? `${fmtQty(r.per_unit)} ${r.uom} / ${r.meter_type === 'KM' ? 'km' : r.meter_type === 'KWH' ? 'kWh' : 'hr'}` : '—'}</td>
              </tr>
            ))}
            {!data.report.length && <tr><td colSpan={6} style={S.empty}>No fuel issued in this period.</td></tr>}
          </tbody>
        </table>
      </div>
      <p style={S.sub}>Fuel per unit needs two or more fills with meter readings in the period; the first fill is left out because it was used before the first reading.</p>
      <h4 style={{ margin: '1rem 0 0.5rem' }}>Fuel log</h4>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Date</th><th style={S.th}>Equipment</th><th style={S.th}>Fuel</th><th style={{ ...S.th, ...S.num }}>Qty</th><th style={{ ...S.th, ...S.num }}>Reading</th><th style={{ ...S.th, ...S.num }}>Value</th><th style={S.th}>Doc</th><th style={S.th}>By</th></tr></thead>
          <tbody>
            {data.log.map((l) => (
              <tr key={l.id}>
                <td style={S.td}>{fmtDate(l.fuel_date)}</td><td style={S.td}>{l.asset_name}</td><td style={S.td}>{l.item_name}</td>
                <td style={{ ...S.td, ...S.num }}>{fmtQty(l.qty)} {l.base_uom}</td>
                <td style={{ ...S.td, ...S.num }}>{l.meter_reading != null ? `${fmtQty(l.meter_reading)} ${String(l.meter_type).toLowerCase()}` : '—'}</td>
                <td style={{ ...S.td, ...S.num }}>{fmtMoney(l.value)}</td><td style={S.td}>{l.doc_no}</td><td style={S.td}>{l.created_by}</td>
              </tr>
            ))}
            {!data.log.length && <tr><td colSpan={8} style={S.empty}>No fuel issued in this period.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Settings: module moves + module stores (Hotel Admin) ─────────────────
function LegacyMove({ api, kind, onChanged }) {
  const [info, setInfo] = useState(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const load = useCallback(() => { api(`/migrate/${kind}`).then(setInfo).catch((e) => setErr(e.message)); }, [api, kind]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setBusy(true); setErr('');
    try { const r = await api(`/migrate/${kind}`, { method: 'POST', body: { confirm } }); setResult(r.summary); setConfirm(''); load(); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  if (!info) return err ? <div style={S.err}>{err}</div> : <div style={S.empty}>Loading…</div>;
  const c = info.counts || {};
  const sm = result || info.done?.summary;
  return (
    <div style={{ ...S.card, maxWidth: 820 }}>
      <h4 style={{ margin: '0 0 0.4rem' }}>Move {info.label.toLowerCase()} into Stores</h4>
      <p style={{ ...S.sub, marginTop: 0 }}>
        {kind === 'amenity'
          ? 'Amenity items, stock and history move into the Housekeeping store. Room-type templates stay as they are; when housekeeping confirms a room, the amenities are taken from Stores.'
          : 'Spare parts, stock and usage history move into the Maintenance store. Spare usage on a work order is then issued from Stores at FIFO cost.'}
      </p>
      {err && <div style={S.err}>{err}</div>}
      {info.done ? (
        <div style={{ ...S.ok, background: '#f8fafc', borderColor: '#e2e8f0', color: '#334155' }}>Moved on {new Date(info.done.at).toLocaleString('en-IN')} by {info.done.by || '—'}.</div>
      ) : !info.available ? (
        <div style={S.empty}>Nothing to move on this server.</div>
      ) : (
        <>
          <table style={S.table}><tbody>
            <tr><td style={S.td}>Items (active)</td><td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{c.items} ({c.active_items})</td></tr>
            <tr><td style={S.td}>Items with stock / value</td><td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{c.with_stock} / {fmtMoney(c.stock_value)}</td></tr>
            <tr><td style={S.td}>History rows</td><td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{c.history}</td></tr>
            <tr><td style={S.td}>Negative stock (comes over as zero)</td><td style={{ ...S.td, ...S.num, fontWeight: 700, color: c.negative ? '#b45309' : undefined }}>{c.negative}</td></tr>
          </tbody></table>
          <div style={{ ...S.bar, marginTop: '0.75rem' }}>
            <Field label="Type MIGRATE to confirm"><input style={{ ...S.inp, width: 160 }} value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
            <button type="button" className="btn-primary" onClick={run} disabled={busy || confirm !== 'MIGRATE'}>{busy ? 'Moving…' : 'Move now'}</button>
          </div>
        </>
      )}
      {sm && (
        <p style={{ ...S.sub }}>
          Into {sm.store}: {sm.items} items, {sm.history_rows} history rows, {sm.stock_lines} with stock ({fmtMoney(sm.stock_value)}).
          {sm.negatives_zeroed?.length ? ` Zeroed: ${sm.negatives_zeroed.join(', ')}.` : ''}{sm.renamed_codes?.length ? ` Renamed: ${sm.renamed_codes.join(', ')}.` : ''}
        </p>
      )}
    </div>
  );
}

export function ModuleMoves({ api, boot, onChanged }) {
  const deptCode = (s) => boot.departments.find((d) => d.id === s.department_id)?.code;
  const [stores, setStores] = useState({ hk_amenity_store_id: boot.settings?.hk_amenity_store_id || '', mnt_issue_store_id: boot.settings?.mnt_issue_store_id || '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const save = async () => {
    setErr(''); setMsg('');
    try { await api('/settings/modules', { method: 'PUT', body: stores }); setMsg('Saved.'); onChanged(); } catch (e) { setErr(e.message); }
  };
  return (
    <>
      <div style={{ ...S.card, maxWidth: 820 }}>
        <h4 style={{ margin: '0 0 0.4rem' }}>Stores used by Housekeeping and Maintenance</h4>
        {err && <div style={S.err}>{err}</div>}
        {msg && <div style={S.ok}>{msg}</div>}
        <div style={S.bar}>
          <Field label="Room amenities are taken from">
            <Select style={{ ...S.inp, minWidth: 220 }} value={stores.hk_amenity_store_id} onChange={(e) => setStores({ ...stores, hk_amenity_store_id: e.target.value })}>
              <option value="">Housekeeping main store (HK-MAIN)</option>
              {boot.stores.filter((s) => s.is_active && deptCode(s) === 'HK').map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
            </Select>
          </Field>
          <Field label="Work-order spares are issued from">
            <Select style={{ ...S.inp, minWidth: 220 }} value={stores.mnt_issue_store_id} onChange={(e) => setStores({ ...stores, mnt_issue_store_id: e.target.value })}>
              <option value="">Maintenance main store (MNT-MAIN)</option>
              {boot.stores.filter((s) => s.is_active && deptCode(s) === 'MNT').map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
            </Select>
          </Field>
          <button type="button" className="btn-primary" onClick={save}>Save</button>
        </div>
      </div>
      <LegacyMove api={api} kind="amenity" onChanged={onChanged} />
      <LegacyMove api={api} kind="spare" onChanged={onChanged} />
    </>
  );
}
